import { useState } from 'react';
import { useTranslation } from '@mukil/i18n';
import { Alert, Button, GlassCard, Input } from '@jumpstone/ui-react';
import { ScreenHeader } from '../../components/common/screen_header';
import { REPORT_DESCRIPTION_KEY, REPORT_NAME_KEY } from '../../lib/label_keys';
import { usePayrollReports } from '../../data/use_pay_runs';
import { useOAuthEntityContext } from '../../lib/auth/oauth_entity_context';
import { triggerDownload } from '../../lib/download';
import {
  fetchStatutoryReport,
  STATUTORY_REPORT_KIND,
  type StatutoryReportKind,
} from '../../lib/auth/oauth_statutory_reports';
import type { PayrollReportId } from '../../data/types';
import { minReportYear } from './payroll_reports_utils';

type ExportErrorReason =
  | 'entitlement_required'
  | 'not_available'
  | 'missing_entity'
  | 'error';

/** Current-month ISO string (`YYYY-MM`) at render time — sensible default period. */
function currentMonth(): string {
  const now = new Date();
  const y = now.getUTCFullYear();
  const m = String(now.getUTCMonth() + 1).padStart(2, '0');
  return `${String(y)}-${m}`;
}

function currentYear(): number {
  return new Date().getUTCFullYear();
}

/**
 * The payroll report catalogue (#23307). Each card exposes a period picker
 * (month or year, per {@link STATUTORY_REPORT_KIND}) and an Export button
 * that fetches the CSV and triggers a browser download via
 * `lib/download.ts`'s `triggerDownload`. The 5 backend procedures are
 * OAuth-allowlisted for `payroll:employer:read`; a session-cookie caller with
 * entity access reaches them identically (the global gate no-ops).
 */
export function PayrollReportsScreen(): React.JSX.Element {
  const { t } = useTranslation('wp');
  const { report_ids } = usePayrollReports();

  return (
    <>
      <ScreenHeader
        title={t('reports.title')}
        description={t('reports.subtitle')}
      />
      <div className="grid gap-3 grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
        {report_ids.map((id) => (
          <StatutoryReportCard key={id} reportId={id} />
        ))}
      </div>
    </>
  );
}

function StatutoryReportCard({
  reportId,
}: {
  reportId: PayrollReportId;
}): React.JSX.Element {
  const { t } = useTranslation('wp');
  const entityContext = useOAuthEntityContext();
  const kind: StatutoryReportKind = STATUTORY_REPORT_KIND[reportId];
  const [month, setMonth] = useState<string>(currentMonth);
  const [year, setYear] = useState<number>(currentYear);
  const [isExporting, setIsExporting] = useState(false);
  const [errorReason, setErrorReason] = useState<ExportErrorReason | null>(
    null,
  );

  async function handleExport(): Promise<void> {
    setErrorReason(null);
    if (entityContext.status !== 'ready') {
      setErrorReason('missing_entity');
      return;
    }
    setIsExporting(true);
    try {
      const result = await fetchStatutoryReport(
        reportId,
        {
          firm_id: entityContext.firm_id,
          entity_id: entityContext.entity_id,
        },
        kind === 'monthly' ? { month } : { year },
      );
      if (result.ok) {
        await triggerDownload(
          new Blob([result.csv_content], { type: result.mime_type }),
          result.filename,
        );
      } else if (result.reason === 'error') {
        console.error(
          '[wagepro-web] statutory report export failed',
          result.message,
        );
        setErrorReason('error');
      } else {
        setErrorReason(result.reason);
      }
    } catch (err) {
      console.error('[wagepro-web] statutory report export threw', err);
      setErrorReason('error');
    } finally {
      setIsExporting(false);
    }
  }

  return (
    <GlassCard className="rounded-2xl p-5 flex flex-col">
      <h2 className="font-bold mb-1">{t(REPORT_NAME_KEY[reportId])}</h2>
      <p className="text-[12.5px] text-[color:var(--ink-muted)] mb-3 flex-1">
        {t(REPORT_DESCRIPTION_KEY[reportId])}
      </p>
      <div className="flex flex-wrap items-center gap-2 mb-2">
        {kind === 'monthly' ? (
          <Input
            type="month"
            size="sm"
            aria-label={t('reports.period_month_label')}
            value={month}
            onChange={(e) => {
              setMonth(e.target.value);
            }}
          />
        ) : (
          <Input
            type="number"
            size="sm"
            min={minReportYear(reportId)}
            max={2100}
            aria-label={t('reports.period_year_label')}
            value={year}
            onChange={(e) => {
              const parsed = parseInt(e.target.value, 10);
              if (!Number.isNaN(parsed)) setYear(parsed);
            }}
          />
        )}
        <Button
          variant="secondary"
          size="xs"
          disabled={isExporting}
          onClick={() => {
            void handleExport();
          }}
        >
          {isExporting ? t('reports.exporting') : t('common.export')}
        </Button>
      </div>
      {errorReason === 'entitlement_required' && (
        <Alert
          variant="danger"
          title={t('reports.export_entitlement_required')}
        />
      )}
      {errorReason === 'not_available' && (
        <Alert variant="warning" title={t('reports.export_not_available')} />
      )}
      {errorReason === 'missing_entity' && (
        <Alert variant="warning" title={t('reports.export_missing_entity')} />
      )}
      {errorReason === 'error' && (
        <Alert variant="danger" title={t('reports.export_error')} />
      )}
    </GlassCard>
  );
}
